import React, { useMemo, useState } from "react";
import { getAvatarColor, getInitials } from "../../../utils/avatarUtils";
import "./SiteStaffAssignmentPanel.scss";

export const getStaffDisplayName = (row) =>
  row?.StaffName ||
  [row?.FirstName, row?.LastName].filter(Boolean).join(" ") ||
  "Unknown";

export const filterStaffRows = (items, searchTerm) => {
  const query = String(searchTerm || "").trim().toLowerCase();
  if (!query) return items;

  return items.filter((row) =>
    [row.StaffName, row.FirstName, row.LastName, row.EmployeeNum, row.RoleName]
      .filter(Boolean)
      .join(" ")
      .toLowerCase()
      .includes(query),
  );
};

export const StaffRowSummary = ({ row }) => {
  const name = getStaffDisplayName(row);
  return (
    <p className="site-staff-row__summary" title={name}>
      <span className="site-staff-row__name">{name}</span>
      {row.EmployeeNum ? (
        <span className="site-staff-row__sep"> · {row.EmployeeNum}</span>
      ) : null}
      {row.RoleName ? (
        <span className="site-staff-row__sep"> · {row.RoleName}</span>
      ) : null}
    </p>
  );
};

const SiteStaffAssignmentPanel = ({
  availableStaff = [],
  assignedItems = [],
  onAssignSelected,
  onAssignOne,
  onRemoveStaff,
  assigningStaff = false,
  removingStaffId = "",
  availableTitle = "Available staff",
  availableSubtitle = "Select one or more internal users to add to this site.",
  assignedTitle = "Assigned to site",
  assignedSubtitle = "Staff currently linked to this site.",
  availableSearchPlaceholder = "Search available staff...",
  assignedSearchPlaceholder = "Search assigned staff...",
  readOnly = false,
  className = "",
}) => {
  const [availableSearch, setAvailableSearch] = useState("");
  const [assignedSearch, setAssignedSearch] = useState("");
  const [selectedIds, setSelectedIds] = useState([]);

  const filteredAvailable = useMemo(
    () => filterStaffRows(availableStaff, availableSearch),
    [availableStaff, availableSearch],
  );

  const filteredAssigned = useMemo(
    () => filterStaffRows(assignedItems, assignedSearch),
    [assignedItems, assignedSearch],
  );

  const selectedSet = useMemo(() => new Set(selectedIds), [selectedIds]);
  const allVisibleSelected =
    filteredAvailable.length > 0 &&
    filteredAvailable.every((row) => selectedSet.has(row.StaffId));

  const toggleStaff = (staffId) => {
    setSelectedIds((prev) =>
      prev.includes(staffId)
        ? prev.filter((id) => id !== staffId)
        : [...prev, staffId],
    );
  };

  const toggleSelectAllVisible = () => {
    if (allVisibleSelected) {
      const visibleIds = new Set(filteredAvailable.map((row) => row.StaffId));
      setSelectedIds((prev) => prev.filter((id) => !visibleIds.has(id)));
      return;
    }

    const merged = new Set([
      ...selectedIds,
      ...filteredAvailable.map((row) => row.StaffId),
    ]);
    setSelectedIds([...merged]);
  };

  const handleBulkAdd = () => {
    if (selectedIds.length === 0) return;
    onAssignSelected(selectedIds);
    setSelectedIds([]);
  };

  const resolvedAssignedSubtitle = readOnly
    ? "Staff linked to this site (view only)."
    : assignedSubtitle;

  const assignedEmptyTitle =
    assignedItems.length === 0
      ? readOnly
        ? "No staff assigned"
        : "No staff assigned yet"
      : "No matching staff";

  const assignedEmptyText =
    assignedItems.length === 0
      ? readOnly
        ? "This site has no linked staff."
        : "Select staff on the left and click Add selected."
      : "Try a different search term.";

  return (
    <div
      className={`site-staff-assignment-panel${
        readOnly ? " site-staff-assignment-panel--read-only" : ""
      } ${className}`.trim()}
    >
      <div
        className={`site-staff-split${
          readOnly ? " site-staff-split--read-only" : ""
        }`}
      >
        {!readOnly && (
        <section className="site-staff-panel site-staff-panel--available">
          <div className="site-staff-panel__header">
            <div>
              <h6 className="site-staff-panel__title">{availableTitle}</h6>
              <p className="site-staff-panel__subtitle">{availableSubtitle}</p>
            </div>
            <span className="site-staff-panel__count">{availableStaff.length}</span>
          </div>

          <div className="site-staff-panel__toolbar">
            <div className="site-staff-search site-staff-search--full">
              <i className="fas fa-search" aria-hidden="true" />
              <input
                type="text"
                placeholder={availableSearchPlaceholder}
                value={availableSearch}
                onChange={(e) => setAvailableSearch(e.target.value)}
              />
            </div>
            <button
              type="button"
              className="btn btn-primary site-staff-bulk-add-btn"
              onClick={handleBulkAdd}
              disabled={assigningStaff || selectedIds.length === 0}
            >
              {assigningStaff ? (
                <>
                  <span
                    className="spinner-border spinner-border-sm me-2"
                    role="status"
                    aria-hidden="true"
                  />
                  Adding...
                </>
              ) : (
                <>
                  <i className="fas fa-user-plus me-2" aria-hidden="true" />
                  Add selected ({selectedIds.length})
                </>
              )}
            </button>
          </div>

          {filteredAvailable.length > 0 && (
            <label className="site-staff-select-all">
              <input
                type="checkbox"
                checked={allVisibleSelected}
                onChange={toggleSelectAllVisible}
              />
              <span>Select all shown</span>
            </label>
          )}

          <div className="site-staff-list site-staff-list--panel">
            {filteredAvailable.length === 0 ? (
              <div className="site-staff-empty site-staff-empty--compact">
                <p className="site-staff-empty__title">
                  {availableStaff.length === 0
                    ? "No staff available"
                    : "No matching staff"}
                </p>
                <p className="site-staff-empty__text">
                  {availableStaff.length === 0
                    ? "All eligible users are already assigned to this site."
                    : "Try a different search term."}
                </p>
              </div>
            ) : (
              filteredAvailable.map((row) => {
                const name = getStaffDisplayName(row);
                const checked = selectedSet.has(row.StaffId);
                return (
                  <div
                    className={`site-staff-row site-staff-row--selectable${
                      checked ? " is-selected" : ""
                    }`}
                    key={row.StaffId}
                  >
                    <input
                      type="checkbox"
                      className="form-check-input site-staff-row__checkbox"
                      checked={checked}
                      onChange={() => toggleStaff(row.StaffId)}
                      aria-label={`Select ${name}`}
                    />
                    <span
                      className="site-staff-row__avatar"
                      style={{ backgroundColor: getAvatarColor(name) }}
                      title={name}
                    >
                      {getInitials(name)}
                    </span>
                    <div className="site-staff-row__main">
                      <StaffRowSummary row={row} />
                    </div>
                    <button
                      type="button"
                      className="btn btn-sm btn-outline-primary site-staff-row__quick-add"
                      onClick={() => onAssignOne(row.StaffId)}
                      disabled={assigningStaff}
                      title={`Add ${name}`}
                    >
                      <i className="fas fa-plus" aria-hidden="true" />
                    </button>
                  </div>
                );
              })
            )}
          </div>
        </section>
        )}

        <section className="site-staff-panel site-staff-panel--assigned">
          <div className="site-staff-panel__header">
            <div>
              <h6 className="site-staff-panel__title">{assignedTitle}</h6>
              <p className="site-staff-panel__subtitle">{resolvedAssignedSubtitle}</p>
            </div>
            <span className="site-staff-panel__count site-staff-panel__count--assigned">
              {assignedItems.length}
            </span>
          </div>

          <div className="site-staff-panel__toolbar site-staff-panel__toolbar--single">
            <div className="site-staff-search site-staff-search--full">
              <i className="fas fa-search" aria-hidden="true" />
              <input
                type="text"
                placeholder={assignedSearchPlaceholder}
                value={assignedSearch}
                onChange={(e) => setAssignedSearch(e.target.value)}
              />
            </div>
          </div>

          <div className="site-staff-list site-staff-list--panel">
            {filteredAssigned.length === 0 ? (
              <div className="site-staff-empty site-staff-empty--compact">
                <div className="site-staff-empty__icon">
                  <i className="fas fa-users" aria-hidden="true" />
                </div>
                <p className="site-staff-empty__title">{assignedEmptyTitle}</p>
                <p className="site-staff-empty__text">{assignedEmptyText}</p>
              </div>
            ) : (
              filteredAssigned.map((row) => {
                const name = getStaffDisplayName(row);
                const isRemoving = removingStaffId === row.StaffId;
                return (
                  <div className="site-staff-row site-staff-row--assigned" key={row.StaffId}>
                    <span
                      className="site-staff-row__avatar"
                      style={{ backgroundColor: getAvatarColor(name) }}
                      title={name}
                    >
                      {getInitials(name)}
                    </span>
                    <div className="site-staff-row__main">
                      <StaffRowSummary row={row} />
                    </div>
                    {!readOnly && (
                    <button
                      type="button"
                      className="btn btn-sm btn-outline-danger site-staff-row__remove"
                      onClick={() => onRemoveStaff(row.StaffId)}
                      disabled={isRemoving || assigningStaff}
                      title={`Remove ${name}`}
                    >
                      {isRemoving ? (
                        <span
                          className="spinner-border spinner-border-sm"
                          role="status"
                          aria-hidden="true"
                        />
                      ) : (
                        <i className="fas fa-times" aria-hidden="true" />
                      )}
                    </button>
                    )}
                  </div>
                );
              })
            )}
          </div>
        </section>
      </div>
    </div>
  );
};

export default SiteStaffAssignmentPanel;
